/**
 * Outil « Moyenne qui nivelle » (Boîte à outils, 6e → 2nde).
 *
 * Niveler (dès la 5e) : des barres qu'on règle au doigt ; ce qui dépasse la
 * moyenne (en orange) comble exactement les creux (en pointillés), puis
 * « Niveler » égalise toutes les barres à la moyenne. Pondérée (dès la 4e) :
 * chaque valeur compte autant de fois que son coefficient ou son effectif.
 * Diagrammes (dès la 6e) : en barres et circulaire depuis un tableau d'effectifs,
 * fréquences et angles dès la 5e.
 */

import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import {
  IconButton,
  Note,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import type { Frac } from '../../ui/exact';
import { formatNumber, frenchSpaces } from '../../ui/format';
import { FIGURE_BG_CLASS, HaloText, INK, MUTED } from '../../ui/svg-parts';
import {
  DATASETS,
  axisStep,
  excessDeficit,
  fv,
  mean,
  meanText,
  shares,
  show,
  sum,
  weightedMean,
  weightedText,
  type Category,
  type Row,
} from './mean';

const TONES = ['#93c5fd', '#fcd34d', '#86efac', '#f9a8d4', '#c4b5fd', '#fdba74'];
const STROKE = '#1f2d55';
const EXCESS = '#f97316';
const MAXV = 20;
/** graduations lisibles (ink-400 est trop pâle sur fond clair) */
const AXIS_TEXT = 'var(--color-ink-600)';

/* ---------- animation 0 → 1 ---------- */

function useLevel() {
  const [t, setT] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const stop = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  };
  useEffect(() => stop, []);
  const go = (target: 0 | 1) => {
    stop();
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setT(target);
      return;
    }
    timer.current = setInterval(() => {
      setT((v) => {
        const n = target === 1 ? Math.min(1, v + 0.05) : Math.max(0, v - 0.05);
        if (n === target) stop();
        return n;
      });
    }, 30);
  };
  const reset = () => {
    stop();
    setT(0);
  };
  return { t, go, reset };
}

/* ---------- barres à niveler ---------- */

const BW = 340;
const BH = 250;
const PX0 = 34;
const PX1 = 330;
const PY0 = 16;
const PY1 = 226;

function Bars({
  values,
  tones,
  t,
  selected,
  onPick,
  label,
  top = MAXV,
  labels,
}: {
  values: readonly number[];
  /** couleur de chaque barre */
  tones: readonly string[];
  t: number;
  selected: number | null;
  onPick?: (i: number, v: number) => void;
  label: string;
  /** haut de l'axe (20 pour des notes) */
  top?: number;
  /** étiquette sous chaque barre (null : rien) */
  labels?: ReadonlyArray<string | null>;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const yOf = (v: number) => PY1 - (v / top) * (PY1 - PY0);
  const tick = top <= 6 ? 1 : 2;
  const labelEvery = top <= 6 ? 1 : 4;
  const n = values.length;
  const slot = (PX1 - PX0) / n;
  const w = Math.min(34, slot - 6);
  const m = fv(mean(values));
  const ed = excessDeficit(values);
  // un toucher (clic) règle la barre ; un geste pour faire défiler la page ne change rien
  const pick = (e: MouseEvent<SVGSVGElement>) => {
    if (!onPick) return;
    const r = ref.current?.getBoundingClientRect();
    if (!r || !r.width) return;
    const px = ((e.clientX - r.left) / r.width) * BW;
    const py = ((e.clientY - r.top) / r.height) * BH;
    if (px < PX0 || px > PX1) return;
    const i = Math.min(n - 1, Math.floor((px - PX0) / slot));
    const v = Math.max(0, Math.min(top, Math.round(((PY1 - py) / (PY1 - PY0)) * top)));
    onPick(i, v);
  };
  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${BW} ${BH}`}
      role="img"
      aria-label={label}
      onClick={pick}
      className={cn(
        'border-ink-100 w-full max-w-[420px] touch-manipulation rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
    >
      {/* axe gradué */}
      {Array.from({ length: top / tick + 1 }, (_, k) => {
        const v = k * tick;
        return (
          <g key={v}>
            <line
              x1={PX0}
              x2={PX1}
              y1={yOf(v)}
              y2={yOf(v)}
              stroke="var(--color-ink-100)"
              strokeWidth={1}
            />
            {v % labelEvery === 0 && (
              <text x={PX0 - 6} y={yOf(v) + 4} fontSize={12} textAnchor="end" fill={AXIS_TEXT}>
                {v}
              </text>
            )}
          </g>
        );
      })}
      <line x1={PX0} x2={PX0} y1={PY0} y2={PY1} stroke={INK} strokeWidth={1.2} />
      <line x1={PX0} x2={PX1} y1={PY1} y2={PY1} stroke={INK} strokeWidth={1.2} />
      {values.map((x, i) => {
        const h = x + (m - x) * t;
        const cx = PX0 + slot * (i + 0.5);
        const above = t === 0 ? ed.above.find((a) => a.i === i) : undefined;
        const below = t === 0 ? ed.below.find((a) => a.i === i) : undefined;
        return (
          <g key={i}>
            <rect
              x={cx - w / 2}
              y={yOf(above ? m : h)}
              width={w}
              height={yOf(0) - yOf(above ? m : h)}
              fill={tones[i % tones.length]}
              stroke={STROKE}
              strokeWidth={selected === i ? 2.6 : 1}
            />
            {/* lignes d'unités */}
            {Array.from({ length: Math.floor(h) }, (_, k) => (
              <line
                key={k}
                x1={cx - w / 2}
                x2={cx + w / 2}
                y1={yOf(k + 1)}
                y2={yOf(k + 1)}
                stroke={STROKE}
                strokeOpacity={0.18}
              />
            ))}
            {above && (
              <rect
                x={cx - w / 2}
                y={yOf(x)}
                width={w}
                height={yOf(m) - yOf(x)}
                fill={EXCESS}
                fillOpacity={0.85}
                stroke={STROKE}
                strokeWidth={selected === i ? 2.6 : 1}
              />
            )}
            {below && (
              <rect
                x={cx - w / 2}
                y={yOf(m)}
                width={w}
                height={yOf(x) - yOf(m)}
                fill="none"
                stroke="var(--color-primary-500)"
                strokeWidth={1.6}
                strokeDasharray="4 3"
              />
            )}
            {(labels ? labels[i] : String(x)) !== null && (
              <text x={cx} y={PY1 + 16} fontSize={12} textAnchor="middle" fill={INK}>
                {labels ? labels[i] : String(x)}
              </text>
            )}
          </g>
        );
      })}
      <line
        x1={PX0}
        x2={PX1}
        y1={yOf(m)}
        y2={yOf(m)}
        stroke="var(--color-primary-500)"
        strokeWidth={1.8}
        strokeDasharray="7 4"
      />
      <HaloText
        x={PX1}
        y={yOf(m) - 6}
        fontSize={12}
        fontWeight={700}
        textAnchor="end"
        fill="var(--color-primary-600)"
      >
        {`moyenne ${show(mean(values), true)}`}
      </HaloText>
    </svg>
  );
}

function LevelButtons({ lv }: { lv: ReturnType<typeof useLevel> }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button type="button" className={PRIMARY_BUTTON} onClick={() => lv.go(1)}>
        Niveler
      </button>
      <button type="button" className={SECONDARY_BUTTON} onClick={() => lv.go(0)}>
        Remettre les barres
      </button>
    </div>
  );
}

/** « ce qui dépasse = ce qui manque » */
function BalanceText({ values }: { values: readonly number[] }) {
  const ed = excessDeficit(values);
  if (ed.above.length === 0)
    return <span>Toutes les valeurs sont égales : la moyenne est cette valeur.</span>;
  // décimaux s'ils tombent juste, sinon fractions exactes (jamais d'arrondis qu'on additionne)
  const decimal = [...ed.above, ...ed.below].every((a) => 100 % a.d.q === 0);
  const txt = (f: Frac) =>
    decimal ? formatNumber(f.p / f.q, 2) : f.q === 1 ? String(f.p) : `${f.p}/${f.q}`;
  // « 2 + 3 = 5 », ou seulement « 5 » s'il n'y a qu'un terme
  const list = (xs: typeof ed.above, total: Frac) =>
    xs.length > 1 ? `${xs.map((a) => txt(a.d)).join(' + ')} = ${txt(total)}` : txt(total);
  return (
    <span>
      {frenchSpaces(
        `Ce qui dépasse la moyenne (en orange) : ${list(ed.above, ed.excess)}. Ce qui manque (en pointillés) : ${list(ed.below, ed.deficit)}. C’est pareil : en comblant les creux avec ce qui dépasse, toutes les barres arrivent à la même hauteur, la moyenne.`,
      )}
    </span>
  );
}

/* ---------- Niveler ---------- */

function LevelMode() {
  const [values, setValues] = useState<number[]>([12, 7, 15, 9, 13]);
  const [sel, setSel] = useState<number | null>(0);
  const lv = useLevel();
  const set = (i: number, v: number) => {
    lv.reset();
    setValues(values.map((x, k) => (k === i ? v : x)));
  };
  return (
    <div className="space-y-3">
      <p className="text-ink-600 text-sm">
        {frenchSpaces('Touche une barre à la hauteur voulue (de 0 à 20), puis nivelle.')}
      </p>
      <Bars
        values={values}
        tones={[TONES[0]!]}
        t={lv.t}
        selected={sel}
        onPick={(i, v) => {
          setSel(i);
          set(i, v);
        }}
        label={`${values.length} barres : ${values.join(', ')} ; moyenne ${show(mean(values), true)}`}
      />
      <div className="flex flex-wrap items-center gap-2">
        {sel !== null && sel < values.length && (
          <div className="w-44">
            <Stepper
              label={`Barre ${sel + 1}`}
              value={values[sel]!}
              min={0}
              max={MAXV}
              onChange={(v) => set(sel, v)}
            />
          </div>
        )}
        <IconButton
          label="Ajouter une barre"
          disabled={values.length >= 8}
          onClick={() => {
            lv.reset();
            setValues([...values, 10]);
            setSel(values.length);
          }}
        >
          <Plus className="h-5 w-5" aria-hidden />
        </IconButton>
        <IconButton
          label="Retirer la barre choisie"
          disabled={values.length <= 2 || sel === null}
          onClick={() => {
            if (sel === null) return;
            lv.reset();
            setValues(values.filter((_, k) => k !== sel));
            setSel(null);
          }}
        >
          <Trash2 className="h-5 w-5" aria-hidden />
        </IconButton>
      </div>
      <div role="group" aria-label="Choisir une barre" className="flex flex-wrap gap-1">
        {values.map((x, i) => (
          <ToggleChip key={i} on={sel === i} onClick={() => setSel(i)}>
            {frenchSpaces(`${i + 1} : ${x}`)}
          </ToggleChip>
        ))}
      </div>
      <LevelButtons lv={lv} />
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(`Moyenne : ${show(mean(values))}`)}
      </p>
      <Note live={false}>
        <span className="block font-semibold">
          {frenchSpaces(`Moyenne = ${meanText(values)}.`)}
        </span>
        <span className="mt-1 block">
          <BalanceText values={values} />
        </span>
      </Note>
    </div>
  );
}

/* ---------- Pondérée ---------- */

const WEIGHTED_PRESETS: ReadonlyArray<{
  id: string;
  label: string;
  value: string;
  weight: string;
  rows: Row[];
}> = [
  {
    id: 'notes',
    label: 'Notes et coefficients',
    value: 'Note',
    weight: 'Coefficient',
    rows: [
      { x: 12, n: 2 },
      { x: 8, n: 1 },
      { x: 15, n: 3 },
    ],
  },
  {
    id: 'freres',
    label: 'Valeurs et effectifs',
    value: 'Valeur',
    weight: 'Effectif',
    rows: [
      { x: 0, n: 4 },
      { x: 1, n: 6 },
      { x: 2, n: 3 },
      { x: 3, n: 1 },
    ],
  },
];

function WeightedMode() {
  const [preset, setPreset] = useState(WEIGHTED_PRESETS[0]!);
  const [rows, setRows] = useState<Row[]>(WEIGHTED_PRESETS[0]!.rows);
  const lv = useLevel();
  const total = sum(rows.map((r) => r.n));
  const values = rows.flatMap((r) => Array<number>(r.n).fill(r.x));
  const tones = rows.flatMap((r, k) => Array<string>(r.n).fill(TONES[k % TONES.length]!));
  const setRow = (k: number, r: Row) => {
    lv.reset();
    setRows(rows.map((x, i) => (i === k ? r : x)));
  };
  const plain = mean(rows.map((r) => r.x));
  const w = weightedMean(rows);
  return (
    <div className="space-y-3">
      <div role="group" aria-label="Exemple" className="flex flex-wrap gap-2">
        {WEIGHTED_PRESETS.map((p) => (
          <ToggleChip
            key={p.id}
            on={p.id === preset.id}
            onClick={() => {
              lv.reset();
              setPreset(p);
              setRows(p.rows);
            }}
          >
            {p.label}
          </ToggleChip>
        ))}
      </div>
      <ul className="space-y-2">
        {rows.map((r, k) => (
          <li
            key={k}
            className="flex flex-wrap items-center gap-1.5 border-l-4 pl-1.5"
            style={{ borderColor: TONES[k % TONES.length] }}
          >
            <div className="w-[132px]">
              <Stepper
                label={`${preset.value} ${k + 1}`}
                labelAbove
                value={r.x}
                min={0}
                max={MAXV}
                onChange={(v) => setRow(k, { ...r, x: v })}
              />
            </div>
            <div className="w-[132px]">
              <Stepper
                label={`${preset.weight} ${k + 1}`}
                labelAbove
                value={r.n}
                min={1}
                max={Math.min(8, r.n + (20 - total))}
                onChange={(v) => setRow(k, { ...r, n: v })}
              />
            </div>
            <IconButton
              label={`Retirer la ligne ${k + 1}`}
              disabled={rows.length <= 2}
              onClick={() => {
                lv.reset();
                setRows(rows.filter((_, i) => i !== k));
              }}
            >
              <Trash2 className="h-5 w-5" aria-hidden />
            </IconButton>
          </li>
        ))}
      </ul>
      {rows.length < 5 && total < 20 && (
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={() => {
            lv.reset();
            setRows([...rows, { x: 10, n: 1 }]);
          }}
        >
          Ajouter une ligne
        </button>
      )}
      <Bars
        values={values}
        tones={tones}
        t={lv.t}
        selected={null}
        top={Math.max(4, Math.ceil(Math.max(...rows.map((r) => r.x)) / 2) * 2)}
        labels={rows.flatMap((r) =>
          Array.from({ length: r.n }, (_, j) => (j === 0 ? String(r.x) : null)),
        )}
        label={`${total} barres (chaque valeur répétée selon son ${preset.weight.toLowerCase()}) ; moyenne pondérée ${show(w, true)}`}
      />
      <LevelButtons lv={lv} />
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(`Moyenne pondérée : ${show(w)}`)}
      </p>
      <Note live={false}>
        <span className="block font-semibold">
          {frenchSpaces(`Moyenne pondérée = ${weightedText(rows)}.`)}
        </span>
        <span className="mt-1 block">
          {frenchSpaces(
            `Chaque valeur compte autant de fois que son ${preset.weight.toLowerCase()} : on divise par ${total}, pas par ${rows.length}. Sans en tenir compte, on trouverait ${meanText(rows.map((r) => r.x))}${
              fv(plain) === fv(w)
                ? ' : ici, la même chose.'
                : `, ${fv(plain) < fv(w) ? 'moins' : 'plus'} que la moyenne pondérée.`
            }`,
          )}
        </span>
      </Note>
    </div>
  );
}

/* ---------- Diagrammes ---------- */

function BarChart({ cats }: { cats: readonly Category[] }) {
  const max = Math.max(1, ...cats.map((c) => c.n));
  const step = axisStep(max);
  const top = Math.ceil(max / step) * step;
  const y = (v: number) => PY1 - (v / top) * (PY1 - PY0);
  const slot = (PX1 - PX0) / cats.length;
  return (
    <svg
      viewBox={`0 0 ${BW} ${BH}`}
      role="img"
      aria-label={`Diagramme en barres : ${cats.map((c) => `${c.label} ${c.n}`).join(', ')}`}
      className={cn('border-ink-100 w-full max-w-[420px] rounded-2xl border', FIGURE_BG_CLASS)}
    >
      {Array.from({ length: top / step + 1 }, (_, k) => (
        <g key={k}>
          <line x1={PX0} x2={PX1} y1={y(k * step)} y2={y(k * step)} stroke="var(--color-ink-100)" />
          <text x={PX0 - 6} y={y(k * step) + 4} fontSize={12} textAnchor="end" fill={AXIS_TEXT}>
            {k * step}
          </text>
        </g>
      ))}
      <line x1={PX0} x2={PX0} y1={PY0} y2={PY1} stroke={INK} strokeWidth={1.2} />
      <line x1={PX0} x2={PX1} y1={PY1} y2={PY1} stroke={INK} strokeWidth={1.2} />
      {cats.map((c, i) => {
        const cx = PX0 + slot * (i + 0.5);
        return (
          <g key={c.label}>
            <rect
              x={cx - 9}
              y={y(c.n)}
              width={18}
              height={PY1 - y(c.n)}
              fill={TONES[i % TONES.length]}
              stroke={STROKE}
            />
            <text
              x={cx}
              y={y(c.n) - 5}
              fontSize={12}
              textAnchor="middle"
              fill={INK}
              fontWeight={700}
            >
              {c.n}
            </text>
            <text x={cx} y={PY1 + 16} fontSize={12} textAnchor="middle" fill={INK}>
              {c.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function PieChart({ cats }: { cats: readonly Category[] }) {
  const N = sum(cats.map((c) => c.n));
  const cx = BW / 2;
  const cy = 122;
  const R = 100;
  const pt = (a: number, r = R): [number, number] => [cx + r * Math.sin(a), cy - r * Math.cos(a)];
  let a0 = 0;
  return (
    <svg
      viewBox={`0 0 ${BW} ${BH}`}
      role="img"
      aria-label={`Diagramme circulaire : ${cats.map((c) => `${c.label} ${c.n} sur ${N}`).join(', ')}`}
      className={cn('border-ink-100 w-full max-w-[420px] rounded-2xl border', FIGURE_BG_CLASS)}
    >
      {N === 0 ? (
        <circle cx={cx} cy={cy} r={R} fill="none" stroke={MUTED} strokeDasharray="4 4" />
      ) : (
        cats.map((c, i) => {
          const a = (c.n / N) * 2 * Math.PI;
          const start = a0;
          a0 += a;
          if (c.n === 0) return null;
          const [x1, y1] = pt(start);
          const [x2, y2] = pt(start + a);
          const large = a > Math.PI ? 1 : 0;
          const d =
            c.n === N
              ? `M${cx - R},${cy} a${R},${R} 0 1,0 ${2 * R},0 a${R},${R} 0 1,0 ${-2 * R},0`
              : `M${cx},${cy} L${x1.toFixed(1)},${y1.toFixed(1)} A${R},${R} 0 ${large},1 ${x2.toFixed(1)},${y2.toFixed(1)} Z`;
          const [lx, ly] = pt(start + a / 2, R * 0.62);
          return (
            <g key={c.label}>
              <path d={d} fill={TONES[i % TONES.length]} stroke={STROKE} strokeWidth={1.2} />
              {a > 0.45 && (
                <text
                  x={lx}
                  y={ly}
                  fontSize={12}
                  textAnchor="middle"
                  fill={STROKE}
                  fontWeight={700}
                >
                  <tspan x={lx} dy={-2}>
                    {c.label}
                  </tspan>
                  <tspan x={lx} dy={14}>
                    {c.n}
                  </tspan>
                </text>
              )}
            </g>
          );
        })
      )}
    </svg>
  );
}

function ChartsMode({ rank }: { rank: number }) {
  const [dsId, setDsId] = useState(DATASETS[0]!.id);
  const [counts, setCounts] = useState<Record<string, number[]>>({});
  const [kind, setKind] = useState<'bars' | 'pie'>('bars');
  const ds = DATASETS.find((d) => d.id === dsId)!;
  const ns = counts[dsId] ?? ds.cats.map((c) => c.n);
  const cats = ds.cats.map((c, i) => ({ label: c.label, n: ns[i]! }));
  const N = sum(ns);
  const sh = shares(cats);
  return (
    <div className="space-y-3">
      <div role="group" aria-label="Exemple" className="flex flex-wrap gap-2">
        {DATASETS.map((d) => (
          <ToggleChip key={d.id} on={d.id === dsId} onClick={() => setDsId(d.id)}>
            {d.title}
          </ToggleChip>
        ))}
      </div>
      <Segmented
        ariaLabel="Diagramme"
        value={kind}
        options={[
          { value: 'bars', label: 'En barres' },
          { value: 'pie', label: 'Circulaire' },
        ]}
        onChange={setKind}
      />
      {kind === 'bars' ? <BarChart cats={cats} /> : <PieChart cats={cats} />}
      <ul className="space-y-2" aria-label="Tableau des effectifs">
        {cats.map((c, i) => (
          <li key={c.label} className="flex flex-wrap items-center gap-2">
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ background: TONES[i % TONES.length] }}
              aria-hidden
            />
            <div className="w-40">
              <Stepper
                label={c.label}
                value={c.n}
                min={0}
                max={40}
                format={(v) => String(v)}
                onChange={(v) =>
                  setCounts({ ...counts, [dsId]: ns.map((x, k) => (k === i ? v : x)) })
                }
              />
            </div>
            {rank >= 2 && (
              <span className="text-ink-600 text-xs">
                {frenchSpaces(
                  `fréquence ${sh[i]!.freqText}${kind === 'pie' ? ` ; angle ${sh[i]!.angleText}` : ''}`,
                )}
              </span>
            )}
          </li>
        ))}
      </ul>
      <Note live={false}>
        {frenchSpaces(
          rank >= 2
            ? `Effectif total : ${N}. Fréquence = effectif ÷ effectif total ; la somme des fréquences fait 1 (100 %).${
                kind === 'pie'
                  ? ' Dans le diagramme circulaire, l’angle d’un secteur est proportionnel à l’effectif : angle = fréquence × 360°.'
                  : ''
              }`
            : `Effectif total : ${N}. ${
                kind === 'pie'
                  ? 'Plus l’effectif est grand, plus la part du disque est grande : la moitié de l’effectif total occupe la moitié du disque.'
                  : 'La hauteur de chaque barre est l’effectif : on la lit sur l’axe gradué.'
              }`,
        )}
      </Note>
    </div>
  );
}

export function MeanLevelingTool({ level }: { level?: string | null }) {
  const rank = Math.min(5, Math.max(1, schoolLevelRank(level) ?? 3));
  const modes = [
    ...(rank >= 2 ? [{ value: 'level' as const, label: 'Moyenne' }] : []),
    ...(rank >= 3 ? [{ value: 'weighted' as const, label: 'Pondérée' }] : []),
    { value: 'charts' as const, label: 'Diagrammes' },
  ];
  const [mode, setMode] = useState<'level' | 'weighted' | 'charts'>(modes[0]!.value);
  return (
    <div className="space-y-4">
      {modes.length > 1 && (
        <Segmented ariaLabel="Ce qu’on fait" value={mode} options={modes} onChange={setMode} />
      )}
      {mode === 'level' && <LevelMode />}
      {mode === 'weighted' && <WeightedMode />}
      {mode === 'charts' && <ChartsMode rank={rank} />}
    </div>
  );
}
